import React from 'react';

import { Typography } from '@mui/material';

import RoomMappingPicker from '../RoomMappingPicker';
import type { RoomMappingRow } from '../RoomMappingPicker';

import type { PackageRoomSource } from './roomMapping';

const ROOM_SOURCE_CHIPS: Record<PackageRoomSource, RoomMappingRow['chip']> = {
  matching: { color: 'info', label: 'MATCHING SF' },
  override: { label: 'OVERRIDE' },
};

export interface PackageRoomRow {
  packageId: string;
  packageName: string;
  roomAssetName?: string;
  source?: PackageRoomSource;
}

interface Props {
  disabled?: boolean;
  onSelectRoomType: (packageId: string, roomKey: string) => void;
  roomTypeOptions: Array<{ id: string; name: string }>;
  rows: Array<PackageRoomRow>;
  selectedRoomTypeIdByPackageId: Record<string, string>;
}

// Only rooms with an applied rate are offered, since a package can't link to any other.
export default function PackageRoomPicker({
  disabled,
  onSelectRoomType,
  roomTypeOptions,
  rows,
  selectedRoomTypeIdByPackageId,
}: Props) {
  if (rows.length === 0 || roomTypeOptions.length === 0) {
    return null;
  }

  const pickerRows = rows.map(
    (row): RoomMappingRow => ({
      chip: row.source && ROOM_SOURCE_CHIPS[row.source],
      id: row.packageId,
      roomKey: selectedRoomTypeIdByPackageId[row.packageId] ?? '',
      subtitle: row.roomAssetName && (
        <Typography variant="caption" color="text.secondary">
          Salesforce room: {row.roomAssetName}
        </Typography>
      ),
      title: row.packageName,
    }),
  );

  return (
    <RoomMappingPicker
      description={[
        'Each package is automatically mapped based on its linked SF room asset. Override per-package room type here.',
      ]}
      disabled={disabled}
      onSelectRoom={onSelectRoomType}
      roomOptions={roomTypeOptions}
      rows={pickerRows}
      selectIdPrefix="package-room"
      title="Room for each package"
    />
  );
}
